import { utils } from '@hh/global.js'

const { debounce } = utils

class HHFiltersWrapper extends HTMLElement {
  constructor() {
    super()
    this.filterData = []
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this)
    this.onlyItem = this.getAttribute('data-item-only')
    this.enableUpdateFilter = true

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event)
    }, 500)

    this.querySelector('form').addEventListener(
      'input',
      this.debouncedOnSubmit.bind(this)
    )

    this.querySelectorAll('label[for]').forEach(label => {
      label.addEventListener('keydown', e => {
        if (e.key !== ' ' && e.key !== 'Enter') return
    
        e.preventDefault()
    
        const input = document.getElementById(label.htmlFor)
        if (!input || input.checked) return
    
        input.checked = true
    
        input.dispatchEvent(new Event('input', { bubbles: true }))
        input.dispatchEvent(new Event('change', { bubbles: true }))
    
        const modal = this.closest('hh-details-modal')
        if (this.onlyItem && modal) {
          modal.toggle(false, false, true)
          modal.close()
        }
      })
    })
    

    window.addEventListener('popstate', this.onHistoryChange.bind(this))

    if (!this.onlyItem)
      window.addEventListener('load-more-grid', this.onLoadMoreGrid.bind(this))

    this.bindActiveFacetButtonEvents()

    this.updateSortCountBubble()
    this.mobileMediaQuery = window.matchMedia('(max-width: 749px)')
    this.mobileMediaQuery.addEventListener?.('change', () => this.updateSortCountBubble())
  }

  onSubmitHandler(event) {
    event.preventDefault()
    const formData = new FormData(event.target.closest('form'))

    if (this.onlyItem && this.closest('hh-details-modal')) {
      this.closest('hh-details-modal').toggle(false, false, true)
    }

    if (this.onlyItem) {
      const currentSearchParams = new URLSearchParams(window.location.search)
      currentSearchParams.set(this.onlyItem, formData.get(this.onlyItem))
      this.renderPage(currentSearchParams.toString(), event)
    } else {
      const searchParams = new URLSearchParams(formData).toString()
      this.renderPage(searchParams, event)
    }
  }

  connectedCallback() {
    this.syncSortRadios()
  }

  onActiveFilterClick(event) {
    event.preventDefault()
    this.toggleActiveFacets()
    this.renderPage(new URL(event.currentTarget.href).searchParams.toString())
  }

  onHistoryChange(event) {
    const searchParams = event.state?.searchParams || ''
    this.renderPage(searchParams, null, false)
  }

  toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable)
    })
  }

  onLoadMoreGrid(event) {
    this.rendering = true
    const value = event.detail.value
    if (value?.params) this.renderPage(value?.params, 'none', true, 'append')
  }

  renderPage(searchParams, event = '', updateURLHash = true, mode = 'replace') {
    document
      .getElementById('CollectionProductGrid')
      .querySelector('.js-product-grid')
      .classList.add('loading')

    const url =
      `${window.location.pathname}?` +
      this.getSections()
        .map((section) => `sections[]=${section.section}`)
        .join('&') +
      `&${searchParams}`

    const filterDataUrl = (element) => element.url === url

    this.filterData.some(filterDataUrl) && mode !== 'append'
      ? this.renderSectionFromCache(filterDataUrl, event, mode)
      : this.renderSectionFromFetch(
        `${window.location.pathname}?` + `&${searchParams}`,
        event,
        mode
      )

    if (updateURLHash && mode !== 'append') this.updateURLHash(searchParams)
  }

  async renderSectionFromFetch(url, event, mode) {
    try {
      const response = await fetch(url)
      const pageContent = await response.text()

      this.filterData = [...this.filterData, { pageContent, url }]
      this.renderFilters(pageContent, event)
      this.renderProductGrid(pageContent, mode)
      this.rendering = false
    } catch (error) {
      console.error('Error fetching or parsing the URL:', error)
    }
  }

  renderSectionFromCache(filterDataUrl, event, mode) {
    const html = this.filterData.find(filterDataUrl).html
    this.renderFilters(html, event)
    this.renderProductGrid(html, mode)
  }

  renderProductGrid(html, mode) {
    if (mode == 'append') {
      const gridHtml = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('ul#main-collection-product-grid').innerHTML

      document
        .querySelector('ul#main-collection-product-grid')
        .insertAdjacentHTML('beforeend', gridHtml)
    } else {
      const innerHTML = new DOMParser()
        .parseFromString(html, 'text/html')
        .getElementById('CollectionProductGrid').innerHTML

      document.getElementById('CollectionProductGrid').innerHTML = innerHTML
    }
  }

  renderFilters(...args) {
    this.renderSingleFormFilters('#CollectionFiltersForm', ...args)
    this.renderSingleFormFilters('#CollectionFiltersFormMobile', ...args)
  }

  renderSingleFormFilters(form, html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
    const expandedElements = []
    if (document.querySelector(`${form}`)) {
      Array.from(document.querySelectorAll(`${form} .js-filter`)).forEach(el => {
        if (el.hasAttribute('open')) expandedElements.push(el.id)
      })

      document.querySelector(`${form}`).innerHTML = parsedHTML.querySelector(`${form}`).innerHTML
      this.syncSortRadios(document.querySelector(form))
      Array.from(document.querySelectorAll(`${form} .js-filter`)).forEach(el => {
        if (expandedElements.includes(el.id)) el.setAttribute('open', '')
      })
    }

    const facetDetailsElements = parsedHTML.querySelectorAll(
      `${form} .js-filter`
    )
    const matchesIndex = (element) =>
      element.dataset.index ===
      event?.target?.closest('.js-filter')?.dataset.index

    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex)

    this.renderBubble(parsedHTML)

    if (countsToRender) {
      this.renderCounts(countsToRender, event?.target?.closest('.js-filter'))
    }
  }

  renderCustomItem(html, selector) {
    const inHtml = html.querySelector(selector)
    if (!inHtml) return
    document
      .querySelectorAll(selector)
      .forEach((element) => (element.innerHTML = inHtml.innerHTML))
  }

  renderBubble(html) {
    this.renderCustomItem(html, '.js-bubble')
    this.renderCustomItem(html, '.js-product-count')
    this.renderCustomItem(html, '.js-results-label')
    this.renderCustomItem(html, '.js-sort-active-value')
    this.renderCustomItem(html, '.js-load-more')

    this.bindActiveFacetButtonEvents()
    this.toggleActiveFacets(false)
    this.updateSortCountBubble()
  }

  updateSortCountBubble() {
    if (!window.matchMedia('(max-width: 749px)').matches) return

    const hasSort = new URLSearchParams(location.search).has('sort_by')
    const modal = this.closest('hh-details-modal')
    if (!modal) return

    const src = modal.querySelector('.filters-modal__trigger .js-bubble')
    const base = src ? parseInt((src.textContent.match(/\d+/) || ['0'])[0], 10) || 0 : 0
    const total = hasSort ? base + 1 : base

    modal.querySelectorAll('.filters-modal__trigger .js-bubble, .filters-modal__clear .js-bubble')
      .forEach(bubble => { bubble.textContent = total > 0 ? `(${total})` : '' })
  }

  renderCounts(source, target) {
    const countElementSelectors = ['.js-bubble', '.js-selected']
    countElementSelectors.forEach((selector) => {
      const targetElement = target?.querySelector(selector)
      const sourceElement = source.querySelector(selector)

      if (sourceElement && targetElement) {
        target.querySelector(selector).outerHTML =
          source.querySelector(selector).outerHTML
      }
    })
  }

  bindActiveFacetButtonEvents() {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.addEventListener('click', this.onActiveFilterClick, {
        once: true,
      })
    })
  }

  updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    )
  }

  syncSortRadios() {
    const currentSortParams = new URLSearchParams(location.search).get('sort_by')
    if (!currentSortParams) return
    document
      .querySelectorAll('.collection-filters__sort-container input[name="sort_by"]')
      .forEach(radio => { radio.checked = (radio.value === currentSortParams) })
  }

  getSections() {
    return [
      {
        id: 'main-collection-filters',
        section: document.getElementById('main-collection-filters').dataset.id,
      },
      {
        id: 'main-collection-product-grid',
        section: document.getElementById('main-collection-product-grid').dataset
          .id,
      },
    ]
  }
}

customElements.define('hh-filters-wrapper', HHFiltersWrapper)
